import { cn } from 'src/lib/utils';
import { portalDs } from 'src/components/portal-design-system';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import type { DataJobRunSummary } from '../types/data-jobs.types';
import { DataJobStatusBadgeV2 } from './data-job-status-badge-v2';
import { PortalDsTableLoading } from './etl-detail-table-v2';
import { PortalDsAlert } from 'src/components/portal-design-system';

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) {
    return '—';
  }
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s`;
}

interface DataJobRunListV2Props {
  runs: DataJobRunSummary[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onSelect: (run: DataJobRunSummary) => void;
}

export function DataJobRunListV2({
  runs,
  isLoading,
  isError,
  onSelect,
}: DataJobRunListV2Props) {
  if (isLoading) {
    return <PortalDsTableLoading message="Loading ETL runs…" />;
  }

  if (isError) {
    return (
      <PortalDsAlert variant="danger" title="Could not load ETL runs">
        Try refreshing the page or check your connection.
      </PortalDsAlert>
    );
  }

  if (!runs?.length) {
    return (
      <p className={cn('py-8 text-center', portalDs.bodySm)}>
        No runs match the current filters.
      </p>
    );
  }

  const headers = ['Job', 'Status', 'Trigger', 'Date', 'Created', 'Duration', 'Summary'];

  return (
    <div className={portalDs.tableContainer}>
      <Table>
        <TableHeader>
          <TableRow className={portalDs.tableHead}>
            {headers.map((header) => (
              <TableHead key={header} className={portalDs.tableHeadCell}>
                {header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.map((run) => (
            <TableRow
              key={run.id}
              className={cn(portalDs.tableBodyRow, portalDs.tableBodyRowInteractive)}
              onClick={() => onSelect(run)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(run);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={`Open run ${run.job_label}`}
            >
              <TableCell className={cn(portalDs.tableCell, 'font-semibold')}>
                {run.job_label}
              </TableCell>
              <TableCell className={portalDs.tableCell}>
                <DataJobStatusBadgeV2 status={run.status} />
              </TableCell>
              <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted, 'capitalize')}>
                {run.trigger_source}
              </TableCell>
              <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                {run.date_range ?? run.active_date ?? '—'}
              </TableCell>
              <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                {formatDisplayDateTime(run.created_at)}
              </TableCell>
              <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                {formatDuration(run.duration_seconds)}
              </TableCell>
              <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted, 'max-w-xs truncate')}>
                {run.summary ?? run.error_summary ?? '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
